import React, { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import {
 DndContext,
 DragEndEvent,
 PointerSensor,
 TouchSensor,
 useSensor,
 useSensors,
 useDraggable,
 useDroppable,
} from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { RefreshCw, GripVertical, UserRound, CheckCircle2, Loader2 } from "lucide-react"
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
 ModernButton,
 Toast,
 ToastType,
 ListPageSkeleton,
} from "../../components/ui"
import { apiService } from "../../services/api"
import type { Sale, SaleOperationalKanbanColumn } from "../../types"
import { formatCurrency } from "../../utils/formatters"
import { cn } from "@/lib/utils"

function formatDurationMs(ms: number): string {
 const totalSec = Math.max(0, Math.floor(ms / 1000))
 const m = Math.floor(totalSec / 60)
 const h = Math.floor(m / 60)
 const d = Math.floor(h / 24)
 if (d > 0) return `${d}d ${h % 24}h`
 if (h > 0) return `${h}h ${m % 60}m`
 if (m > 0) return `${m}m`
 return `${totalSec}s`
}

type PreparationQueueItem = {
 id: string
 sale: Sale
 kanbanColumn?: SaleOperationalKanbanColumn | null
}

function saleMeetsHandoffBayRules(sale: Sale): boolean {
 const tasks = sale.preparationTasks ?? []
 if (tasks.length === 0) {
  return Boolean(sale.deliveryResponsibleId)
 }
 return tasks.every((task) => Boolean(task.assigneeId))
}

function preparationResponsibleLabel(sale: Sale, t: TFunction): string {
 if (sale.operationalKanbanColumn?.responsibleUser?.name) {
  return sale.operationalKanbanColumn.responsibleUser.name
 }
 return t("operation.kanban.columnResponsibleEmpty")
}

function DroppableColumn({
  id,
  title,
  responsibleLabel,
  disabled,
  children,
  className,
  items,
}: {
  id: string
  title: string
  responsibleLabel: string
  disabled?: boolean
  children: React.ReactNode
  className?: string
  items?: Sale[]
}) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled: Boolean(disabled) })
  
  const progressInfo = useMemo(() => {
    if (!items || items.length === 0) return null
    let totalDone = 0
    let totalItems = 0
    for (const item of items) {
      const progress = item.operationalProgress as any
      if (progress) {
        totalDone += (progress.done || 0)
        totalItems += (progress.total || 0)
      }
    }
    if (totalItems === 0) return null
    return { done: totalDone, total: totalItems }
  }, [items])

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex flex-col rounded-xl border border-border bg-card/50 min-h-[280px] p-2",
        "min-w-[min(280px,calc(100vw-2.75rem))] flex-shrink-0 snap-start",
        "md:min-w-[200px] md:flex-1",
        isOver && "ring-2 ring-accent ring-offset-2 ring-offset-background",
        className
      )}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1 pb-2 flex items-center gap-2">
        {title}
        {progressInfo && (
          <span className={cn(
            "text-[10px] px-1.5 py-0.5 rounded-full",
            progressInfo.done === progressInfo.total 
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
          )}>
            {progressInfo.done}/{progressInfo.total} concluídos
          </span>
        )}
      </h3>
      <p className="px-1 pb-2 text-[11px] text-muted-foreground truncate">{responsibleLabel}</p>
      <div className="flex flex-col gap-2 flex-1">{children}</div>
    </div>
  )
}

function DraggableSaleCard({
  sale,
  clock,
  t,
  isLastColumn,
}: {
  sale: Sale
  clock: number
  t: TFunction
  isLastColumn?: boolean
}) {
 void clock
 const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
 id: sale.id,
 })
const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.45 : 1,
    transition: isDragging ? 'none' : 'transform 200ms ease-out',
  }
 const navigate = useNavigate()
 const cust = sale.customer?.name ?? "—"
 const veh = sale.vehicle
 const vehLabel = veh
 ? `${veh.brand?.name ?? ""} ${veh.model?.name ?? ""}`.trim() || "—"
 : "—"
 const plateRaw = veh?.identification?.plate ?? (veh as { plate?: string } | undefined)?.plate
 const plate = plateRaw?.trim()
const pct =
  sale.operationalProgress && (sale.operationalProgress as any).total > 0
    ? Math.round(
    ((sale.operationalProgress as any).done /
    (sale.operationalProgress as any).total) *
    100
    )
  : null
const entered = sale.operationalKanbanColumnEnteredAt
  const durationLabel =
  entered && !Number.isNaN(new Date(entered).getTime())
  ? formatDurationMs(Date.now() - new Date(entered).getTime())
  : null
  const deliveryAtRaw = isLastColumn ? sale.deliveryAt : null
  const deliveryLabel =
  deliveryAtRaw && !Number.isNaN(new Date(deliveryAtRaw).getTime())
  ? new Date(deliveryAtRaw).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  : null
  const responsible = preparationResponsibleLabel(sale, t)

 return (
 <div ref={setNodeRef} style={style} className="flex flex-col gap-1.5">
 <button
 type="button"
 className="w-full rounded-lg border border-border bg-background p-3 text-left shadow-sm hover:border-accent/40 transition-colors flex gap-2"
 onClick={() => navigate(`/operation/preparation/${sale.id}`)}
 >
 <span
 className="mt-0.5 touch-none text-muted-foreground cursor-grab active:cursor-grabbing shrink-0"
 {...listeners}
 {...attributes}
 onClick={(e) => e.stopPropagation()}
 >
 <GripVertical className="h-4 w-4" />
 </span>
 <span className="min-w-0 flex-1">
 <span className="font-medium text-sm block truncate">{cust}</span>
 <span className="text-xs text-muted-foreground block truncate">{vehLabel}</span>
 {plate ? (
 <span className="text-[10px] text-muted-foreground mt-0.5 block font-mono">{plate}</span>
 ) : null}
 <span className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1.5 truncate">
 <UserRound className="h-3 w-3 shrink-0 opacity-80" />
 <span className="truncate">{responsible}</span>
 </span>
 <span className="text-[10px] text-muted-foreground mt-1 block">
 {formatCurrency(parseFloat(String(sale.finalValue)))}
 </span>
{durationLabel ? (
  <span className="text-[10px] text-muted-foreground mt-0.5 block">{durationLabel}</span>
  ) : null}
  {deliveryLabel ? (
  <span className="text-[10px] text-accent mt-0.5 block font-medium">
    Entrega: {deliveryLabel}
  </span>
  ) : null}
  {pct !== null && (
 <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
 <div className="h-full bg-accent rounded-full" style={{ width: `${pct}%` }} />
 </div>
 )}
 </span>
 </button>
 </div>
 )
}

export const OperationPreparationKanbanPage: React.FC = () => {
 const { t } = useTranslation()
 const [sales, setSales] = useState<Sale[]>([])
 const [queueItems, setQueueItems] = useState<PreparationQueueItem[]>([])
 const [columns, setColumns] = useState<SaleOperationalKanbanColumn[]>([])
 const [loading, setLoading] = useState(true)
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
 const [clock, setClock] = useState(0)
 const [movingQueueId, setMovingQueueId] = useState<string | null>(null)
 const [draggedSaleId, setDraggedSaleId] = useState<string | null>(null)
 const [assigneeNameById, setAssigneeNameById] = useState<Record<string, string>>({})

 useEffect(() => {
 const id = window.setInterval(() => setClock((c) => c + 1), 30000)
 return () => window.clearInterval(id)
 }, [])

 const sensors = useSensors(
 useSensor(PointerSensor, {
 activationConstraint: { distance: 8 },
 }),
 useSensor(TouchSensor, {
 activationConstraint: { delay: 220, tolerance: 6 },
 })
 )

 const load = useCallback(async () => {
 try {
 setLoading(true)
   const [queueRes, cols, assigneesRes] = await Promise.all([
    apiService.getPreparationQueue({ limit: 200, page: 1, statuses: ["queued", "in_progress"] }),
    apiService.getSaleKanbanColumns("preparation"),
    apiService.getPreparationAssignees(),
   ])
   const items = Array.isArray(queueRes?.items) ? (queueRes.items as PreparationQueueItem[]) : []
   setQueueItems(items)
   setSales(
    items.map((item) => ({
     ...item.sale,
     operationalKanbanColumn: item.kanbanColumn ?? item.sale.operationalKanbanColumn,
    }))
   )
 const list = Array.isArray(cols) ? cols : []
 setColumns(list.filter((c: SaleOperationalKanbanColumn) => c.isActive))
   const assignees = Array.isArray(assigneesRes) ? assigneesRes : []
   setAssigneeNameById(
    Object.fromEntries(
     assignees
      .filter((user) => user?.id && user?.name)
      .map((user) => [user.id, user.name])
    )
   )
 } catch {
 setToast({ message: t("operation.loadError"), type: "error" })
 setSales([])
   setQueueItems([])
 setColumns([])
   setAssigneeNameById({})
 } finally {
 setLoading(false)
 }
 }, [t])

 useEffect(() => {
 void load()
 }, [load])

 const activeColumnIds = useMemo(() => new Set(columns.map((c) => c.id)), [columns])

 const byColumn = useMemo(() => {
 const map: Record<string, Sale[]> = {}
 for (const c of columns) {
 map[c.id] = []
 }
 const fallbackId = columns[0]?.id
 for (const s of sales) {
 const cid = s.operationalKanbanColumn?.id
 const key =
 cid && map[cid] !== undefined ? cid : fallbackId && map[fallbackId] !== undefined ? fallbackId : null
 if (key) {
 map[key].push(s)
 }
 }
 return map
 }, [sales, columns])

const moveToColumn = async (saleId: string, queueItemId: string, targetColumnId: string) => {
  const sale = sales.find((x) => x.id === saleId)
  const currentId = sale?.operationalKanbanColumn?.id ?? null
  if (!sale || currentId === targetColumnId) return
  const targetColumn = columns.find((c) => c.id === targetColumnId)
  if (
    targetColumn?.isHandoffColumn &&
    !targetColumn.responsibleUserId &&
    !saleMeetsHandoffBayRules(sale)
  ) {
    setToast({ message: t("operation.kanban.bayRequiresAssignee"), type: "error" })
    return
  }
  const currentIndex = columns.findIndex((item) => item.id === currentId)
  const targetIndex = columns.findIndex((item) => item.id === targetColumnId)
  const lastIndex = columns.length - 1
  if (currentIndex === lastIndex && targetIndex !== lastIndex) {
    setToast({ message: t("operation.kanban.cannotMoveBackFromLast"), type: "error" })
    return
  }
  if (currentIndex >= 0 && Math.abs(targetIndex - currentIndex) !== 1) {
    setToast({ message: t("operation.kanban.moveOnlyAdjacent"), type: "error" })
    return
  }

  const targetCol = columns.find((c) => c.id === targetColumnId)
  setSales((prev) =>
    prev.map((s) =>
      s.id === saleId
        ? { ...s, operationalKanbanColumn: targetCol ?? s.operationalKanbanColumn }
        : s
    )
  )
  setMovingQueueId(queueItemId)

  try {
    await apiService.patchPreparationQueueItem(queueItemId, {
      kanbanColumnId: targetColumnId,
    })
    setToast({ message: t("operation.kanban.moved"), type: "success" })
  } catch (err) {
    setSales((prev) =>
      prev.map((s) =>
        s.id === saleId
          ? { ...s, operationalKanbanColumn: columns.find((c) => c.id === currentId) ?? sale.operationalKanbanColumn }
          : s
      )
    )
    setToast({ message: t("operation.kanban.moveError"), type: "error" })
  } finally {
    setMovingQueueId(null)
  }
}

 const onDragEnd = async (e: DragEndEvent) => {
 const { active, over } = e
 if (!over) {
 setDraggedSaleId(null)
 return
 }
 const saleId = String(active.id)
 const targetColumnId = String(over.id)
 if (!activeColumnIds.has(targetColumnId)) {
 setDraggedSaleId(null)
 return
 }
 const queueItem = queueItems.find((item) => item.sale.id === saleId)
 if (!queueItem) {
 setDraggedSaleId(null)
 return
 }
 await moveToColumn(saleId, queueItem.id, targetColumnId)
 setDraggedSaleId(null)
 }

 if (loading) {
 return (
 <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
 <ListPageSkeleton />
 </div>
 )
 }

 return (
 <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6">
 {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
 <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
 <div>
 <h1 className="text-2xl font-bold font-display text-foreground">
 {t("operation.preparation.title")}
 </h1>
 <p className="text-sm text-muted-foreground mt-1">{t("operation.preparation.subtitle")}</p>
 </div>
 <ModernButton type="button" variant="outline" size="sm" onClick={() => void load()}>
 <RefreshCw className="h-4 w-4 mr-2" />
 {t("operation.refresh")}
 </ModernButton>
 </div>

 <Card>
 <CardHeader className="pb-2">
 <CardTitle className="text-base">{t("operation.kanban.boardTitle")}</CardTitle>
 </CardHeader>
 <CardContent className="px-3 sm:px-6">
 {columns.length === 0 ? (
 <p className="text-sm text-muted-foreground py-6 text-center">
 {t("operation.kanban.noColumns")}
 </p>
 ) : (
 <>
 <p className="text-xs text-muted-foreground mb-3 md:hidden">{t("operation.kanban.swipeHint")}</p>
 <DndContext
  sensors={sensors}
  onDragStart={(ev) => setDraggedSaleId(String(ev.active.id))}
  onDragEnd={(ev) => void onDragEnd(ev)}
  onDragCancel={() => setDraggedSaleId(null)}
 >
<div className="flex gap-3 overflow-x-auto overflow-y-visible pb-2 pt-0.5 -mx-1 px-1 snap-x snap-mandatory md:mx-0 md:overflow-visible md:snap-none md:flex-nowrap">
  {columns.map((col) => (
  <DroppableColumn
   key={col.id}
   id={col.id}
   title={col.title}
   responsibleLabel={(() => {
    const name = col.responsibleUser?.name ?? (col.responsibleUserId ? assigneeNameById[col.responsibleUserId] : "")
    return name ? t("operation.kanban.columnResponsible", { name }) : t("operation.kanban.columnResponsibleEmpty")
   })()}
   disabled={(() => {
    if (!draggedSaleId) return false
    const dragged = sales.find((item) => item.id === draggedSaleId)
    const currentIndex = columns.findIndex(
     (item) => item.id === (dragged?.operationalKanbanColumn?.id ?? "")
    )
    const targetIndex = columns.findIndex((item) => item.id === col.id)
    if (currentIndex < 0 || targetIndex < 0) return true
    const lastIndex = columns.length - 1
    if (currentIndex === lastIndex && targetIndex !== lastIndex) return true
    return Math.abs(targetIndex - currentIndex) !== 1
   })()}
   items={byColumn[col.id]}
  >
  {byColumn[col.id]?.map((sale) => {
  return (
<DraggableSaleCard
    key={sale.id}
    sale={sale}
    clock={clock}
    t={t}
    isLastColumn={col.sortOrder === columns[columns.length - 1]?.sortOrder}
   />
  )
  })}
  </DroppableColumn>
  ))}
  </div>
 </DndContext>
 </>
 )}
 </CardContent>
 </Card>
 </div>
 )
}
